import { useState } from 'react';
import { Chessboard } from 'react-chessboard';
import { Chess, Square } from 'chess.js';

interface ChessBoardProps {
  position: string; // FEN string
  onMove: (move: { from: string; to: string; promotion?: string }) => void;
  playerColor: 'white' | 'black';
  disabled?: boolean;
}

export function ChessBoard({ position, onMove, playerColor, disabled }: ChessBoardProps) {
  const chess = new Chess(position);
  const [selectedSquare, setSelectedSquare] = useState<Square | null>(null);
  const [legalMoves, setLegalMoves] = useState<Square[]>([]);

  const handleSquareClick = ({ square }: { piece: any; square: string }) => {
    if (disabled) return;

    const sq = square as Square;
    const currentTurn = chess.turn();
    const playerTurn = playerColor === 'white' ? 'w' : 'b';
    
    if (currentTurn !== playerTurn) return;

    // If clicking on selected square, deselect
    if (selectedSquare === sq) {
      setSelectedSquare(null);
      setLegalMoves([]);
      return;
    }

    // If we have a selected square and clicked on a legal move target
    if (selectedSquare && legalMoves.includes(sq)) {
      const moves = chess.moves({ square: selectedSquare, verbose: true });
      const move = moves.find(m => m.to === sq);
      
      if (move) {
        const promotion = move.promotion ? 'q' : undefined;
        onMove({
          from: selectedSquare,
          to: sq,
          promotion,
        });
        setSelectedSquare(null);
        setLegalMoves([]);
      }
      return;
    }

    // Get piece on clicked square
    const piece = chess.get(sq);
    
    // Only select if it's the player's piece
    if (piece && piece.color === playerTurn) {
      setSelectedSquare(sq);
      const moves = chess.moves({ square: sq, verbose: true });
      setLegalMoves(moves.map(m => m.to as Square));
    } else {
      setSelectedSquare(null);
      setLegalMoves([]);
    }
  };

  const handleDrop = ({ sourceSquare, targetSquare }: { piece: any; sourceSquare: string; targetSquare: string | null }): boolean => {
    if (disabled || !targetSquare) return false;

    // Check if it's the player's turn
    const currentTurn = chess.turn();
    const playerTurn = playerColor === 'white' ? 'w' : 'b';
    
    if (currentTurn !== playerTurn) {
      return false; // Not player's turn
    }

    try {
      // Check if move is legal
      const moves = chess.moves({ square: sourceSquare as Square, verbose: true });
      const move = moves.find(m => m.to === targetSquare);

      if (!move) {
        return false; // Illegal move
      }

      // Handle pawn promotion (default to queen for MVP)
      const promotion = move.promotion ? 'q' : undefined;

      // Send move to backend
      onMove({
        from: sourceSquare,
        to: targetSquare,
        promotion,
      });

      // Clear selection after successful move
      setSelectedSquare(null);
      setLegalMoves([]);

      return true;
    } catch (error) {
      console.error('Invalid move:', error);
      return false;
    }
  };

  // Generate custom square styles for selected square and legal moves
  const getCustomSquareStyles = () => {
    const styles: Record<string, React.CSSProperties> = {};

    // Highlight selected square
    if (selectedSquare) {
      styles[selectedSquare] = {
        backgroundColor: 'rgba(0, 255, 65, 0.4)',
      };
    }

    // Show dots on legal move targets
    legalMoves.forEach(square => {
      const piece = chess.get(square);
      styles[square] = {
        backgroundImage: piece
          ? 'radial-gradient(circle, rgba(0,0,0,.1) 85%, transparent 85%)'
          : 'radial-gradient(circle, rgba(0, 255, 65, 0.3) 25%, transparent 25%)',
        backgroundSize: '100%',
        backgroundPosition: 'center',
      };
    });

    return styles;
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <Chessboard
        options={{
          position: chess.fen(),
          boardOrientation: playerColor,
          onPieceDrop: handleDrop,
          onSquareClick: handleSquareClick,
          squareStyles: getCustomSquareStyles(),
          darkSquareStyle: { backgroundColor: '#2a2a2a' },
          lightSquareStyle: { backgroundColor: '#1a1a1a' },
          dropSquareStyle: {
            boxShadow: 'inset 0 0 1px 6px rgba(0, 255, 65, 0.5)',
          },
          boardStyle: {
            borderRadius: '8px',
            boxShadow: '0 0 20px rgba(0, 255, 65, 0.3)',
          },
        }}
      />
    </div>
  );
}
